/**
 * 2026 縣市長選舉開票中心 — 共用常數
 *
 * 政黨色、縣市對照表與各項可調參數集中於此。
 * 資料格式定義見 docs/election2026/API_CONTRACT.md
 */

/**
 * 政黨對照表
 *
 * key 對應 API 的 `party` 欄位（見契約 §4.1）。
 *
 * - color：長條與地圖方格使用的政黨原色
 * - chipColor：政黨簡稱文字塊的底色。刻意加深以讓白字達到 WCAG AA，
 *              不可與 color 混用（設計稿明訂）
 * - mutedColor：開票率 >= 門檻但未定案時的降飽和色。
 *              等同 color-mix(in srgb, {color} 62%, #9c9c9c)，
 *              此處預先算成 hex 以避開 color-mix 的瀏覽器相容性問題
 * - abbr：卡片上的政黨簡稱
 * - char：地圖方格與席次 bar 使用的單字
 */
export const PARTIES = {
  kmt: { abbr: '國民黨', char: '國', color: '#000095', chipColor: '#000095', mutedColor: '#3b3b98' },
  dpp: { abbr: '民進黨', char: '民', color: '#1B9431', chipColor: '#17812A', mutedColor: '#4c975a' },
  tpp: { abbr: '民眾黨', char: '眾', color: '#28C8C8', chipColor: '#147F7F', mutedColor: '#54b7b7' },
  // 時代力量：2022 苗栗縣長選舉曾有候選人得票逾一成，歷史資料需要正確標示。
  // 黨代表色為黃色，白字對比不足，chipColor 大幅加深以達 AA。
  npp: { abbr: '時代力量', char: '時', color: '#FBBE01', chipColor: '#6E5200', mutedColor: '#c4a03e' },
  independent: { abbr: '無黨籍', char: '無', color: '#8E8E93', chipColor: '#6E6E73', mutedColor: '#939396' }
}

/**
 * 未知政黨的 fallback，避免 API 出現對照表以外的政黨時整頁壞掉
 */
export const PARTY_FALLBACK = PARTIES.independent

/**
 * 取得政黨資訊
 *
 * 對照表以外的政黨（後端以中選會代碼表示，如 `cec-306` 台灣動物保護黨）
 * 顏色沿用中性灰，但**名稱必須用 API 給的 partyName** ——
 * 若一律顯示「無黨籍」，會把小黨候選人誤標為無黨籍，是新聞頁上的事實錯誤。
 *
 * @param {string} party API 的政黨代碼
 * @param {string} [partyName] API 的政黨全名，僅在代碼不在對照表時使用
 * @returns {object} { abbr, char, color, chipColor, mutedColor }
 */
export function getParty(party, partyName) {
  if (PARTIES[party]) {
    return PARTIES[party]
  }
  if (partyName) {
    return Object.assign({}, PARTY_FALLBACK, {
      abbr: partyName,
      char: partyName.slice(0, 1)
    })
  }
  return PARTY_FALLBACK
}

/**
 * 22 縣市對照表
 *
 * 陣列順序即「預設順序」（六都 → 縣市 → 離島），與設計稿一致。
 * slug 決定網址 /election2026/{slug}，是本專案的 SEO 主體，確定後不可變更。
 *
 * cecCode 為 2022 年的中選會行政區代碼，前端不使用，僅供對帳除錯，
 * 需由後端於選前實測確認（見契約 §6）。
 */
export const COUNTIES = [
  // 六都
  { slug: 'taipei', name: '台北市', shortName: '台北', cecCode: '63000' },
  { slug: 'newtaipei', name: '新北市', shortName: '新北', cecCode: '65000' },
  { slug: 'taoyuan', name: '桃園市', shortName: '桃園', cecCode: '68000' },
  { slug: 'taichung', name: '台中市', shortName: '台中', cecCode: '66000' },
  { slug: 'tainan', name: '台南市', shortName: '台南', cecCode: '67000' },
  { slug: 'kaohsiung', name: '高雄市', shortName: '高雄', cecCode: '64000' },
  // 縣市
  { slug: 'keelung', name: '基隆市', shortName: '基隆', cecCode: '10017' },
  { slug: 'hsinchucity', name: '新竹市', shortName: '竹市', cecCode: '10018' },
  { slug: 'hsinchucounty', name: '新竹縣', shortName: '竹縣', cecCode: '10004' },
  { slug: 'miaoli', name: '苗栗縣', shortName: '苗栗', cecCode: '10005' },
  { slug: 'changhua', name: '彰化縣', shortName: '彰化', cecCode: '10007' },
  { slug: 'nantou', name: '南投縣', shortName: '南投', cecCode: '10008' },
  { slug: 'yunlin', name: '雲林縣', shortName: '雲林', cecCode: '10009' },
  { slug: 'chiayicity', name: '嘉義市', shortName: '嘉市', cecCode: '10020' },
  { slug: 'chiayicounty', name: '嘉義縣', shortName: '嘉縣', cecCode: '10010' },
  { slug: 'pingtung', name: '屏東縣', shortName: '屏東', cecCode: '10013' },
  { slug: 'yilan', name: '宜蘭縣', shortName: '宜蘭', cecCode: '10002' },
  { slug: 'hualien', name: '花蓮縣', shortName: '花蓮', cecCode: '10015' },
  { slug: 'taitung', name: '台東縣', shortName: '台東', cecCode: '10014' },
  // 離島
  { slug: 'penghu', name: '澎湖縣', shortName: '澎湖', cecCode: '10016' },
  { slug: 'kinmen', name: '金門縣', shortName: '金門', cecCode: '09020' },
  { slug: 'lienchiang', name: '連江縣', shortName: '連江', cecCode: '09007' }
]

/**
 * slug → 縣市資料的查表用 map
 */
export const COUNTY_MAP = COUNTIES.reduce((map, county) => {
  map[county.slug] = county
  return map
}, {})

/**
 * 預設順序的索引，用於排序時的 tie-breaker
 */
export const COUNTY_ORDER = COUNTIES.reduce((map, county, index) => {
  map[county.slug] = index
  return map
}, {})

/**
 * 卡片上顯示候選人的得票率門檻（%）
 *
 * 台灣選制下多為兩黨對決，但一個選區的登記參選人可能達數十位。
 * 未達此門檻者收合至「其他 N 位」，避免卡片被小額得票者灌爆。
 */
export const CANDIDATE_RATE_THRESHOLD = 10

/**
 * 無論得票率高低都必須顯示的候選人數
 *
 * 用來保底 —— 若第一名大幅領先（例如 95% vs 3% vs 2%），
 * 只套門檻會讓卡片只剩一個人，讀者看不到第二名是誰。
 */
export const MIN_VISIBLE_CANDIDATES = 2

/**
 * 開票前（得票率全為 0）優先呈現的政黨
 *
 * 投票日下午 4 點才開始開票，在那之前所有人都是 0%，得票率門檻無從判斷。
 * 此時改以政黨決定版面：三大黨的候選人列在卡片上，
 * 其餘（無黨籍與小黨）收合至「其他 N 位」。
 */
export const MAJOR_PARTIES = ['kmt', 'dpp', 'tpp']

/**
 * 三段式信心表現的門檻（%）
 *
 * 開票率低於此值時，得票率長條改用 45° 斜線紋理而非滿版實色，
 * 避免讀者誤以為結果已定。設計稿標明此為可調參數。
 */
export const STRIPE_THRESHOLD = 30

/**
 * client 端輪詢間隔（毫秒）。僅在 phase === 'live' 時啟用。
 */
export const POLL_INTERVAL = 30 * 1000

/**
 * 置頂上限
 */
export const PIN_LIMIT = 3

/**
 * 置頂清單的 localStorage key
 */
export const PIN_STORAGE_KEY = 'ctw-election2026-pinned'

/**
 * 排序模式
 */
export const SORT_MODES = [
  { key: 'default', label: '預設順序', hint: '依行政區慣用順序排列（六都 → 縣市 → 離島）' },
  { key: 'gap', label: '差距最小優先', hint: '戰況最激烈的縣市排在最前，未開票的縣市置後' },
  { key: 'counted', label: '開票率高優先', hint: '開票進度最快的縣市排在最前' },
  { key: 'pinnedOnly', label: '只看我置頂的', hint: '僅顯示置頂中的縣市' }
]

/**
 * 資料來源的畫面標示（見契約 §4.3）
 */
export const SOURCE_LABELS = {
  cec: '官方',
  live: '本刊實況',
  manual: '實況數字'
}

/**
 * 相關新聞來源：主站「2026九合一選舉」專題（https://www.ctwant.com/topic/275/）
 *
 * 由編輯台在主站後台維護掛入的文章，前端不另做篩選。
 * ⚠️ 此專題只存在於正式環境，beta 打 `/topic/275` 會回 500（見契約 §7）。
 */
export const NEWS_TOPIC_ID = 275

/**
 * 相關新聞「看更多」的目的地：主站專題頁。
 *
 * 專題內也有非選舉的綜合新聞，因此導到主站專題頁而非本活動的 /election2026/news/。
 * 專題頁使用主站預設版型（含廣告），一律以 <a href> 整頁載入，不用 nuxt-link：
 * 本活動版型刻意不註冊 GAM 廣告，以 SPA 換頁進入時廣告初始化流程與正常進站不同。
 */
export const NEWS_MORE_URL = `/topic/${NEWS_TOPIC_ID}/`

/**
 * 本活動「Mobile 版」的判斷條件，需與 SCSS 的 `@include laptop`（max-width: $laptop = 992px）一致。
 *
 * 只能在 client 端以 matchMedia 使用。不可用 store 的 isMobile：
 * isMobile 的門檻是 576px，且 SSR 時由 user-agent 決定，與本活動的版面切換點不同。
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 992px)'

/**
 * Mobile 浮動相關新聞：進頁後自動展開的延遲（毫秒）
 */
export const NEWS_AUTO_OPEN_DELAY = 3 * 1000

/**
 * Mobile 浮動相關新聞：最新新聞提示的顯示時間（毫秒），底部紅線倒數同步使用
 */
export const NEWS_TOAST_DURATION = 8 * 1000
